import React from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";

import { euro } from "./dashboardFormat";

/*
 * Los gráficos del dashboard comercial, con el lenguaje del mockup de Vir: lo
 * orientativo va rayado, lo valorado en sólido. Hoy todo es orientativo.
 */

/** El rayado de lo orientativo, para fondos CSS. */
export const hatch = (color: string) =>
  `repeating-linear-gradient(45deg, ${color} 0 3px, transparent 3px 7px)`;

/** Muestra de color de una leyenda. */
const Swatch: React.FC<{ background: string; dashed?: boolean }> = ({
  background,
  dashed,
}) => (
  <Box
    component="i"
    sx={{
      display: "inline-block",
      width: 12,
      height: 12,
      borderRadius: 0.5,
      mr: 0.75,
      verticalAlign: "middle",
      background,
      border: dashed ? "1px dashed" : undefined,
      borderColor: "text.secondary",
    }}
  />
);

export const Legend: React.FC<{
  items: { label: string; background: string; dashed?: boolean }[];
}> = ({ items }) => (
  <Stack direction="row" gap={2} flexWrap="wrap" useFlexGap mt={1}>
    {items.map((item) => (
      <Typography key={item.label} variant="caption" color="text.secondary">
        <Swatch background={item.background} dashed={item.dashed} />
        {item.label}
      </Typography>
    ))}
  </Stack>
);

// ----------------------- Funnel -----------------------

export interface FunnelRow {
  label: string;
  /** El número de la barra: las que han llegado. */
  count: number;
  /** Texto pequeño dentro de la barra ("2 activas"). */
  sub?: string;
  /** A la derecha: el importe o un porcentaje. */
  value?: string;
  valueSub?: string;
}

/**
 * Funnel con la conversión entre etapas, como en el mockup: la barra se estrecha con lo
 * que llega a cada fase y entre dos filas se dice qué parte pasa a la siguiente.
 */
export const Funnel: React.FC<{ rows: FunnelRow[]; colors?: string[] }> = ({
  rows,
  colors,
}) => {
  const theme = useTheme();
  const palette = colors ?? [
    theme.palette.grey[500],
    "#6d7c86",
    "#315d82",
    "#b7791f",
    theme.palette.primary.main,
  ];
  const first = Math.max(1, rows[0]?.count ?? 0);
  const max = Math.max(1, ...rows.map((row) => row.count));

  return (
    <Box data-testid="funnel">
      {rows.map((row, index) => {
        const next = rows[index + 1];
        return (
          <React.Fragment key={row.label}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "minmax(90px, 140px) 1fr auto",
                alignItems: "center",
                gap: 1.5,
                py: 0.5,
              }}
            >
              <Typography variant="body2">{row.label}</Typography>
              <Box>
                <Box
                  sx={{
                    width: `${22 + (row.count / max) * 78}%`,
                    backgroundColor: palette[index % palette.length],
                    color: "#fff",
                    borderRadius: 1.5,
                    px: 1.25,
                    py: 0.5,
                    display: "flex",
                    alignItems: "baseline",
                    gap: 1,
                    transition: "width 0.3s ease",
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {row.count}
                  </Typography>
                  {row.sub && (
                    <Typography variant="caption" sx={{ opacity: 0.85 }}>
                      {row.sub}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box sx={{ textAlign: "right" }}>
                {row.value && (
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {row.value}
                  </Typography>
                )}
                {row.valueSub && (
                  <Typography variant="caption" color="text.secondary">
                    {row.valueSub}
                  </Typography>
                )}
              </Box>
            </Box>
            {next && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ pl: { xs: 0, sm: "152px" } }}
              >
                ↓{" "}
                <b>
                  {row.count > 0
                    ? `${Math.round((next.count / row.count) * 100)} %`
                    : "—"}
                </b>{" "}
                pasa a {next.label.toLowerCase()} ·{" "}
                {Math.round((next.count / first) * 100)} % desde el inicio
              </Typography>
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
};

// ----------------------- Recorrido -----------------------

export interface JourneyChip {
  label: string;
  count: number;
  value: string;
  tone?: "win" | "lose" | "slow";
}

/** Una fila de fichas por fase unidas con «›», como el recorrido del mockup. */
export const JourneyRow: React.FC<{
  title: string;
  subtitle: string;
  chips: JourneyChip[];
}> = ({ title, subtitle, chips }) => {
  const theme = useTheme();
  const toneStyle = (tone?: JourneyChip["tone"]) =>
    tone === "win"
      ? {
          borderColor: theme.palette.success.main,
          backgroundColor: theme.palette.action.hover,
        }
      : tone === "lose"
        ? { borderColor: theme.palette.error.main }
        : tone === "slow"
          ? { borderStyle: "dashed" }
          : {};
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "170px 1fr" },
        gap: 1.5,
        alignItems: "center",
        py: 1,
      }}
    >
      <Box>
        <Typography variant="subtitle2">{title}</Typography>
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      </Box>
      <Stack
        direction="row"
        alignItems="center"
        gap={0.5}
        sx={{ overflowX: "auto", pb: 0.5 }}
      >
        {chips.map((chip, index) => (
          <React.Fragment key={chip.label}>
            {index > 0 && (
              <Typography color="text.secondary" aria-hidden>
                ›
              </Typography>
            )}
            <Box
              data-testid={`journey-chip-${chip.label}`}
              sx={{
                flexShrink: 0,
                minWidth: 92,
                border: `1px solid ${theme.palette.grey[300]}`,
                borderRadius: 2,
                px: 1,
                py: 0.75,
                textAlign: "center",
                ...toneStyle(chip.tone),
              }}
            >
              <Typography
                variant="body1"
                sx={{ fontWeight: 700, lineHeight: 1.2 }}
              >
                {chip.count}
              </Typography>
              <Typography variant="caption" display="block" noWrap>
                {chip.label}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                noWrap
              >
                {chip.value}
              </Typography>
            </Box>
          </React.Fragment>
        ))}
      </Stack>
    </Box>
  );
};

// ----------------------- Barras apiladas horizontales -----------------------

export interface StackSegment {
  label: string;
  value: number;
  background: string;
  dashed?: boolean;
}

/** Una barra horizontal partida en segmentos, con su leyenda. */
export const StackBar: React.FC<{
  segments: StackSegment[];
  /** El total de referencia; por defecto, la suma de los segmentos. */
  total?: number;
  height?: number;
  legend?: boolean;
}> = ({ segments, total, height = 22, legend = true }) => {
  const theme = useTheme();
  const max = Math.max(
    1,
    total ?? segments.reduce((sum, segment) => sum + segment.value, 0)
  );
  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          height,
          borderRadius: 1.5,
          overflow: "hidden",
          backgroundColor: theme.palette.grey[200],
        }}
      >
        {segments.map((segment) =>
          segment.value > 0 ? (
            <Box
              key={segment.label}
              title={`${segment.label}: ${euro.format(segment.value)}`}
              sx={{
                width: `${Math.min(100, (segment.value / max) * 100)}%`,
                background: segment.background,
              }}
            />
          ) : null
        )}
      </Box>
      {legend && (
        <Legend
          items={segments.map((segment) => ({
            label: `${segment.label} ${euro.format(segment.value)}`,
            background: segment.background,
            dashed: segment.dashed,
          }))}
        />
      )}
    </Box>
  );
};

// ----------------------- Facturación por mes -----------------------

export interface MonthSeries {
  key: string;
  label: string;
  color: string;
  values: number[];
}

const MONTH_NAMES = [
  "Ene",
  "Feb",
  "Mar",
  "Abr",
  "May",
  "Jun",
  "Jul",
  "Ago",
  "Sep",
  "Oct",
  "Nov",
  "Dic",
];

/** "2026-11" → "Nov 26". */
export const monthLabel = (month: string) => {
  const [year, number] = month.split("-");
  return `${MONTH_NAMES[Number(number) - 1] ?? number} ${year.slice(2)}`;
};

const compactEuro = (value: number) =>
  value >= 1000 ? `${Math.round(value / 1000)}k` : String(Math.round(value));

/**
 * Barras apiladas por mes. Cada serie se dibuja rayada: es orientativo. Cuando haya
 * valoración, las series valoradas irán en sólido.
 */
export const MonthlyBarChart: React.FC<{
  months: string[];
  series: MonthSeries[];
  height?: number;
}> = ({ months, series, height = 240 }) => {
  const theme = useTheme();
  const data = months.map((month, index) => {
    const row: Record<string, number | string> = { month: monthLabel(month) };
    series.forEach((serie) => {
      row[serie.key] = serie.values[index] ?? 0;
    });
    return row;
  });

  return (
    <Box>
      <Box sx={{ width: "100%", height }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{ top: 16, right: 8, left: 0, bottom: 0 }}
          >
            <defs>
              {series.map((serie) => (
                <pattern
                  key={serie.key}
                  id={`hatch-${serie.key}`}
                  width="6"
                  height="6"
                  patternUnits="userSpaceOnUse"
                  patternTransform="rotate(45)"
                >
                  <rect
                    width="6"
                    height="6"
                    fill={serie.color}
                    opacity="0.25"
                  />
                  <line
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="6"
                    stroke={serie.color}
                    strokeWidth="3"
                  />
                </pattern>
              ))}
            </defs>
            <CartesianGrid vertical={false} stroke={theme.palette.grey[200]} />
            <XAxis
              dataKey="month"
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
            />
            <YAxis
              tickFormatter={compactEuro}
              tickLine={false}
              axisLine={false}
              width={44}
              tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
            />
            <ChartTooltip
              formatter={(value: number, name: string) => [
                euro.format(value),
                series.find((serie) => serie.key === name)?.label ?? name,
              ]}
              contentStyle={{
                backgroundColor: theme.palette.background.paper,
                borderColor: theme.palette.grey[300],
                borderRadius: 8,
              }}
            />
            {series.map((serie, index) => (
              <Bar
                key={serie.key}
                dataKey={serie.key}
                stackId="billing"
                fill={`url(#hatch-${serie.key})`}
                stroke={serie.color}
                radius={index === series.length - 1 ? [4, 4, 0, 0] : undefined}
                isAnimationActive={false}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Box>
      <Legend
        items={series.map((serie) => ({
          label: serie.label,
          background: hatch(serie.color),
          dashed: true,
        }))}
      />
    </Box>
  );
};
